// ─────────────────────────────────────────────────────────────────────────────
// Sramika Pricing Engine v3
//
// TWO SEPARATE FARE COMPUTATION PATHS — NEVER MIX THEM:
//
//   computeDriverFare()   → Driver/Cab-Owner view only. No Sramika margin.
//   computeCustomerFare() → Customer view. Adds Convenience Surcharge (TYPE A).
//
// TYPE A  — Sramika Discount: Convenience Surcharge returned as a discount.
//            NEVER visible to driver. Internal margin construct.
// TYPE B  — Cab Partner Offer: when a driver quotes below platform ceiling.
//            Visible to both driver (as "Your Offer") and customer.
//
// Ceiling values come EXCLUSIVELY from the admin pricing API (/pricing?city=).
// Nothing is hardcoded here. See pricingService.js.
// ─────────────────────────────────────────────────────────────────────────────

import { detectCarCategory } from "./carCategory.js";
import { mapToAssociationCategory, getAssociationVehicleIndex } from "./categoryMapping.js";
import { suggestedValue, allVehicleCategories } from "./constants.js";
// ── Internal helpers ──────────────────────────────────────────────────────────

/* The bus tiers, smallest first. A city's rate card is filled in by hand, one category
   at a time, so a tier can exist in code before it exists in that city — and an
   unpriced category used to mean empty rate objects and a fare of zero.

   So: fall back DOWN this ladder to the largest tier the city has actually priced.
   Never up. A 32-seater quoted at the 20-seater's rate is the price it already had
   today; quoted at a smaller tier's rate it would be cheaper than a smaller bus, which
   is the failure these tiers were added to end. The moment the admin fills BUS_32 for
   that city, the exact tier wins and the fallback stops applying. */
const BUS_TIERS = ["MINI_BUS_23", "BUS_24", "BUS_26", "BUS_28", "BUS_32"];

function resolveCityCategory(cityPricing, categoryName) {
  if (!cityPricing) return null;
  if (cityPricing[categoryName]) return categoryName;

  const i = BUS_TIERS.indexOf(categoryName);
  if (i <= 0) return null;
  for (let j = i - 1; j >= 0; j -= 1) {
    if (cityPricing[BUS_TIERS[j]]) return BUS_TIERS[j];
  }
  return null;
}

function resolveRates(car, cityPricing = null) {
  const rawModel = car.carDetails?.model || car.carModel || car.carType || "";
  const seats = car.carDetails?.seats || car.seatCount || "";

  /* An admin's category outranks the detector.
   *
   * The pin has existed for a long time — categorySource ADMIN, written by
   * updateCarCategory, carried across re-approval, protected from write-through,
   * unit-tested, and shown in AdminOwners as "Set by admin" — but the fare never read
   * it. Every quote re-derived the category from the model string, so correcting a car
   * changed a label on one admin screen and not one rupee. A BMW 3 Series detects as
   * MINI_SUV_5 on its model text, and pinning it to SEDAN_5_LUX did nothing at all.
   *
   * Only an ADMIN pin wins. A stored AUTO value is a cached derivation, not a
   * decision, and honouring it would freeze cars at whatever they detected as on the
   * day they were approved — including anything the detector has since learned to do
   * better. The pinned key must still be one the city prices, or it is ignored: a
   * category that was renamed or retired would otherwise quote at nothing. */
  const pinnedKey = car.categorySource === "ADMIN" && car.category ? String(car.category) : null;
  const detected = detectCarCategory(rawModel, seats);
  const effectiveKey = (pinnedKey && allVehicleCategories().includes(pinnedKey)) ? pinnedKey : detected.key;

  const category = { ...detected, key: effectiveKey };

  /* The car's OWN category is tried against the city's card first.
     mapToAssociationCategory carried a hand-written list of nineteen keys and
     returned SEDAN_5_STD for anything else — so every category an admin created was
     quietly priced as a standard saloon. A BMW on SEDAN_5_PREMIUM, with ₹15/km set
     for its tier on the Vijayawada card, was being quoted at the sedan's ₹12. The pin
     above was added precisely so an admin's decision reached the fare, and this line
     threw it away two statements later.
     Asking the city card directly also means this does not depend on the admin
     category config having loaded: the card is data, and it is always there. The
     mapped name stays as the fallback for genuinely legacy keys. */
  const mappedName = mapToAssociationCategory(category.key, rawModel);
  const vi = getAssociationVehicleIndex(category.key, rawModel);

  const pricedAs = resolveCityCategory(cityPricing, category.key)
    || resolveCityCategory(cityPricing, mappedName);
  /* What it was actually priced as. effectiveHeadlineRates reads this back off the
     city card, so a name that does not match the card would hand the grid a different
     set of rates from the ones the fare was built out of. */
  const categoryName = pricedAs || mappedName;
  if (pricedAs) {
    const cp = cityPricing[pricedAs];
    return {
      categoryName,
      // What the money actually came from, so a fare can be explained when it did not
      // come from the car's own tier.
      pricedAsCategory: pricedAs,
      vehicleIndex: vi,
      outstation: cp.outstation || {},
      hourly: cp.hourly || {},
      dayRent: cp.dayRent || {},
      dropping: cp.dropping || {},
    };
  }

  // No fallback association ceiling arrays allowed per Phase 2.5.0 governance.
  return {
    categoryName,
    vehicleIndex: vi,
    outstation: {},
    hourly: {},
    dayRent: {},
    dropping: {},
  };
}

/* A night halt the card names - zero included, which says "none" - or null when it
   names none and the older overnight slab decides. */
const namedNightHalt = (section) => {
  const v = section?.nightHalt;
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/* The night hours the admin sets on the rate card (Admin > Pricing > Night Hours) - the
   same window a hire driver's night halt follows. */
const DEFAULT_NIGHT_START = "22:00";
const DEFAULT_NIGHT_END = "05:00";
const IST_OFFSET_MINUTES = 330;
const MINUTES_PER_DAY = 1440;

const clockMinutes = (v) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(v ?? "").trim());
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
};

/* A pickup as minutes on the clock the trip runs by. The app's own pickup
   ("2026-10-01T06:00") carries no zone and is that clock already; a full timestamp is
   read in India time, so the answer is the same in a phone and on the server. */
function pickupClockMinutes(startAt) {
  if (startAt === undefined || startAt === null || startAt === "") return null;
  if (typeof startAt === "string") {
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(startAt.trim());
    if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5])) / 60000;
  }
  const t = new Date(startAt).getTime();
  return Number.isFinite(t) ? Math.floor(t / 60000) + IST_OFFSET_MINUTES : null;
}

/**
 * How many nights a trip runs into: the admin's night windows (22:00-05:00 unless the
 * card says otherwise) that any part of the trip touches. A night halt is charged for
 * each, and only for these - a 15-hour day from 6 am back by 9 pm has none, a 10-hour
 * run from 8 pm to 6 am has one.
 *
 * null when the pickup time is not known, and the fare falls back to reading a day of
 * more than 12 hours as one that ran into the night.
 */
export function nightsInTrip(startAt, hours, cityPricing) {
  const start = pickupClockMinutes(startAt);
  const length = Number(hours) * 60;
  if (start === null || !(length > 0)) return null;
  const gate = cityPricing?._config?.driverFareLimits || {};
  const from = clockMinutes(gate.nightStart || DEFAULT_NIGHT_START);
  const to = clockMinutes(gate.nightEnd || DEFAULT_NIGHT_END);
  if (from === null || to === null || from === to) return 0;
  const end = start + length;
  let nights = 0;
  // From the window that began the evening before the pickup, to the last one that
  // begins before the trip ends.
  for (let day = Math.floor(start / MINUTES_PER_DAY) - 1; day * MINUTES_PER_DAY + from < end; day++) {
    const opens = day * MINUTES_PER_DAY + from;
    const closes = to > from ? day * MINUTES_PER_DAY + to : (day + 1) * MINUTES_PER_DAY + to;
    if (opens < end && closes > start) nights++;
  }
  return nights;
}

/* Driver bata for a trip: a day's beta for every day of it, and a night halt on top for
   every night it runs into.

   Days are the trip's 24-hour stretches. Nights are counted by the clock when the pickup
   time is known (nightsInTrip) - the admin's night hours decide, not the trip's length -
   and estimated when it is not: a day of more than 12 hours is taken to have run into
   the night, as it always was. */
function calculateDriverBata(durationHrs, pricingSection, nights = null) {
  let b1to8 = Number(pricingSection?.bata1to8);
  let b8to12 = Number(pricingSection?.bata8to12);
  let b12to24 = Number(pricingSection?.bata12to24);

  /* A driver beta for every day, and on top of it a night halt for a day that runs into
     the night - two charges, as the city's taxi association bills them (a Vijayawada
     sedan: Rs 400 + Rs 500). "Bata 1-12 Hrs" and "Night Halt" are the two figures the
     admin's rate card edits, so when a card carries them they decide; the three older
     slabs are read only for a card without them. */
  const dayBeta = Number(pricingSection?.bata1to12);
  const nightHalt = namedNightHalt(pricingSection);

  if (dayBeta > 0) {
    b1to8 = dayBeta;
    b8to12 = dayBeta;
    b12to24 = nightHalt !== null ? dayBeta + nightHalt : (b12to24 || dayBeta);
  } else if (!b1to8 && !b8to12 && !b12to24) {
    const baseBata = Number(pricingSection?.driverBeta) || Number(pricingSection?.driverBata) || 0;
    if (baseBata === 0) return { total: 0, lines: [], nightAllowance: 0, driverAllowance: 0 };
    b1to8 = baseBata - 50;
    b8to12 = baseBata;
    b12to24 = nightHalt !== null ? baseBata + nightHalt : baseBata + 150;
  } else {
    b1to8 = b1to8 || 0;
    b8to12 = b8to12 || 0;
    b12to24 = nightHalt !== null ? b8to12 + nightHalt : (b12to24 || 0);
  }

  /* The night part of an overnight day: what it costs over an ordinary full day - the
     night halt (Rs 500 on a Vijayawada sedan), or on an older card its 550 - 400 = 150.
     For a car owner with their own night halt it is their own figure. */
  const NIGHT_ALLOWANCE_RATE = Math.max(0, b12to24 - b8to12);

  let total = 0;
  let rem = durationHrs;
  let totalDays = 0, cnt12to24 = 0;

  if (nights === null) {
    while (rem > 0) {
      totalDays++;
      if (rem > 12) { cnt12to24++; total += b12to24; }
      else if (rem > 8) { total += b8to12; }
      else { total += b1to8; }
      rem -= 24;
    }
  } else {
    while (rem > 0) {
      totalDays++;
      total += rem > 8 ? b8to12 : b1to8;
      rem -= 24;
    }
    cnt12to24 = Math.max(0, Math.floor(Number(nights)) || 0);
    total += cnt12to24 * NIGHT_ALLOWANCE_RATE;
  }

  const totalHrsRounded = Math.round(durationHrs);
  const nightAllowanceAmt = cnt12to24 * NIGHT_ALLOWANCE_RATE;
  const driverAllowanceAmt = total - nightAllowanceAmt;

  const lines = [];
  if (totalDays > 0) {
    if (driverAllowanceAmt > 0) {
      lines.push({
        label: `Driver Allowance (${totalDays} Day${totalDays > 1 ? "s" : ""} | ${totalHrsRounded}hrs)`,
        value: driverAllowanceAmt,
      });
    }
    if (nightAllowanceAmt > 0) {
      lines.push({
        label: `Night Allowance (${cnt12to24} Night${cnt12to24 > 1 ? "s" : ""})`,
        value: nightAllowanceAmt,
      });
    }
  }

  return { total, lines, nightAllowance: nightAllowanceAmt, driverAllowance: driverAllowanceAmt };
}

// ── TYPE B discount: compare cab partner config vs platform ceilings ──────────

/**
 * Detect cab partner discount (TYPE B) by comparing driver rates vs ceilings.
 * Ceiling values come from the admin pricing API (pricingCeilings param).
 *
 * @param {object} car - Car record with pricingConfig/rates
 * @param {object} rates - Resolved rates for this car's category
 * @param {number} billedKm - Km used in fare calculation
 * @param {object|null} pricingCeilings - Category ceilings from admin API
 * @param {string} categoryName - Vehicle category name
 * @returns {number} cabPartnerDiscountAmount
 */
/* Partner value if they set one; otherwise the SUGGESTED share of the market rate, not
   the market rate itself.

   A blank box means "no preference", not "charge the ceiling". Falling back to the cap
   quoted every unfilled field at the most expensive price the platform permits, which
   made a partner who had simply not finished the form look like the dearest on the
   list. The suggested share is what the onboarding prefill would have written into that
   box, so a blank field and a prefilled one now quote the same fare.

   pct = 100 leaves a value untouched. That is how minKms is passed: it is a distance
   threshold, not a price, and scaling it down would quietly shorten the minimum
   billable trip. Only money goes through the discount. */
/* A partner's figure when they gave one, the market's otherwise.
 *
 * Zero is NOT a figure, in ANY field. A 0 is an empty box with a character in it — a
 * stray keypress, a cleared field, a form that wrote 0 for blank — and it used to be
 * taken literally. For a rate that did not merely zero the price: the outstation
 * candidate is skipped when its rate is not positive, so the trip fell through to a
 * dearer candidate and the customer was quoted MORE than if the partner had left the
 * box alone. On the Vijayawada card, Rs 6,550 against Rs 5,350 — silent, and
 * backwards.
 *
 * minKms follows the same rule by decision, not by oversight. I had excepted it on the
 * argument that zero is a real answer to a distance — "no minimum" — and that treating
 * it as blank hands the partner the market's 320km floor and raises their price. That
 * was considered and overruled: one rule for every field is easier to reason about and
 * to explain to a partner than a field that behaves unlike its neighbours. A partner
 * who genuinely wants no floor sets 1, not 0.
 *
 * Anything not a positive finite number is unset, which also covers a negative and a
 * value that will not parse.
 */
function pick(partnerVal, suggestedVal) {
  const n = Number(partnerVal);
  if (partnerVal !== "" && partnerVal != null && Number.isFinite(n) && n > 0) return n;
  return Number(suggestedVal);
}

const positiveOrNull = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/* Driver bata from the partner's two boxes, in the shape calculateDriverBata charges.
 *
 *   "Driver bata per day" -> bata1to12 (and bata8to12), any working day
 *   "Night halt"          -> nightHalt, charged ON TOP of the day's bata for a day that
 *                            runs into the night - the association bills the two
 *                            separately (Rs 400 + Rs 500 for a Vijayawada sedan), so
 *                            bata12to24, the older overnight total, is day + night
 *   a day under 8 hours   -> bata1to8, kept for older readers in the card's own
 *                            proportion; the fare charges the day's bata for it
 *
 * An empty box takes its suggested value, and a night halt neither names is none.
 * Returns nothing when there is no bata to charge from at all, which leaves the card's
 * slabs as they are. `dayKey` also carries the day figure under its old name, for a card
 * with no slabs, where calculateDriverBata derives them from that one number.
 *
 * Both readings follow the city card (`card`, its section), so the partner's bata is
 * charged the way the market's is. A card without "Bata 1-12 Hrs" charges a day under
 * 8 hours its bata1to8, so the partner's short day is their bata in that proportion. A
 * card that names no night halt reads the night box the older way: the whole overnight
 * bata, the card's bata12to24 when empty. */
function bataSlabs(partnerDay, partnerNight, suggestedDay, suggestedNight, suggested1to8, suggested8to12, dayKey, card = {}) {
  const pDay = positiveOrNull(partnerDay);
  const day = pDay ?? positiveOrNull(suggestedDay);
  if (day === null) return {};
  const s1to8 = positiveOrNull(suggested1to8);
  const s8to12 = positiveOrNull(suggested8to12);
  const shortDay = pDay !== null
    ? (s1to8 !== null && s8to12 !== null ? Math.round(pDay * (s1to8 / s8to12)) : Math.max(0, pDay - 50))
    : (s1to8 ?? Math.max(0, day - 50));
  const perDay = positiveOrNull(card?.bata1to12) !== null ? { bata1to12: day } : {};
  if (namedNightHalt(card) === null) {
    const overnight = positiveOrNull(partnerNight) ?? positiveOrNull(suggestedNight) ?? day + 150;
    return { bata1to8: shortDay, bata8to12: day, ...perDay, bata12to24: overnight, [dayKey]: day };
  }
  const night = positiveOrNull(partnerNight) ?? positiveOrNull(suggestedNight) ?? 0;
  return { bata1to8: shortDay, bata8to12: day, ...perDay, bata12to24: day + night, nightHalt: night, [dayKey]: day };
}



/* The partner's up-to-25 km drop. Their form has no box for it - it asks for 50, 75,
   100 and 125 km - so it follows their 50 km price in the card's own proportion: a
   partner who priced 50 km below the card is below it at 25 km too, and 25 km never
   costs more than their 50 km. No card figure, no band. */
function partnerSlab25(partner50, suggested25, suggested50) {
  const s25 = positiveOrNull(suggested25);
  if (s25 === null) return undefined;
  const p50 = positiveOrNull(partner50);
  const s50 = positiveOrNull(suggested50);
  if (p50 === null || s50 === null) return s25;
  return Math.min(p50, Math.round(p50 * (s25 / s50)));
}

// ── Core trip calculation (shared internal logic) ─────────────────────────────

function _computeTrip(car, hours, distanceKm, tripType, cityPricing, trip = {}) {
  let h = Math.max(1, Number(hours) || 0);
  // Nights by the admin's night hours when the pickup time is known; null otherwise.
  const nights = nightsInTrip(trip?.startAt, h, cityPricing);
  const d = Math.floor(Number(distanceKm) || 0);
  if (d <= 0 && tripType !== "LOCAL") return null;

  const rates = resolveRates(car, cityPricing);
  const { outstation, hourly, dayRent } = rates;

  const totalDriveKm = tripType === "ONE_WAY" ? d * 2 : d;
  const catName = rates.categoryName || "";
  const isMiniOrSedan = catName.toLowerCase().includes("mini") || catName.toLowerCase().includes("sedan");
  /* Mileage for the day-rent fuel charge: the partner's own figure, else the card's -
     computeDriverFare has already put whichever applies into dayRent.fuelKmPerLtr. The
     old guess (10 for a sedan or anything named "mini", 8 otherwise) is kept only for a
     card that carries no mileage at all. */
  const cardMileage = Number(dayRent.fuelKmPerLtr);
  const mileage = Number.isFinite(cardMileage) && cardMileage > 0 ? cardMileage : (isMiniOrSedan ? 10 : 8);
  const fuelPricePerLiter = 100;

  const candidates = [];

  /* One-way drop, from the drop card: a fixed price per distance band up to 125 km,
     then that 125 km price plus the per-km rate for every km beyond it. Beyond is per
     EXTRA km, so the price climbs smoothly past 125 km instead of falling off a cliff
     (charged per km for the whole trip, 126 km would cost less than 125 km).

     The drop price is the whole price - no driver bata on top, the same as an hourly
     package. It is what the partner wrote in their one-way boxes, and the form asks for
     no bata there. A card with no drop prices at all is priced the old way below. */
  const drop = rates.dropping || {};
  const dropNum = (...vals) => {
    for (const v of vals) { const n = Number(v); if (Number.isFinite(n) && n > 0) return n; }
    return null;
  };
  const dropBands = [
    // Up to 25 km - an airport or a town-edge drop - priced on its own; a card without
    // it starts at 50 km as before.
    [25, dropNum(drop.slab25)],
    [50, dropNum(drop.slab50, drop.slab0)],
    [75, dropNum(drop.slab75, drop.slab1)],
    [100, dropNum(drop.slab100, drop.slab2)],
    [125, dropNum(drop.slab125, drop.slab3)],
  ].filter(([, v]) => v !== null);
  const beyondRate = dropNum(drop.beyondRate);
  const dropFare = (() => {
    if (dropBands.length === 0) return null;
    const band = dropBands.find(([km]) => d <= km);
    if (band) return { value: band[1], lines: [{ label: `One-way drop (up to ${band[0]} km)`, value: band[1] }], details: `Drop fare up to ${band[0]} km` };
    if (beyondRate === null) return null;
    const [lastKm, lastValue] = dropBands[dropBands.length - 1];
    const extraKm = d - lastKm;
    const extra = extraKm * beyondRate;
    return {
      value: lastValue + extra,
      lines: [{ label: `One-way drop (first ${lastKm} km)`, value: lastValue }, { label: `Extra ${extraKm} km × ₹${beyondRate}`, value: extra }],
      details: `${lastKm} km drop + ${extraKm} km × ₹${beyondRate}/km`,
    };
  })();

  if (tripType === "ONE_WAY" && dropFare) {
    candidates.push({
      mode: "One-Way Drop",
      baseFare: dropFare.value,
      driverBata: 0, nightAllowance: 0, driverAllowance: 0,
      calculationDetails: dropFare.details,
      billedKm: d,
      isOneWay: true,
      lines: dropFare.lines,
    });
  } else if (tripType === "ONE_WAY") {
    const ratePerKm = Number(outstation.ratePerKm) || 12;
    const dropRatePerKm = ratePerKm * 2 - 2;
    const baseF = d * dropRatePerKm;
    const bataDetails = calculateDriverBata(h, outstation, nights);
    const lines = [{ label: `Drop Fare`, value: d * dropRatePerKm }];
    lines.push(...bataDetails.lines);
    candidates.push({
      mode: "One-Way Drop",
      baseFare: baseF,
      driverBata: bataDetails.total,
      nightAllowance: bataDetails.nightAllowance,
      driverAllowance: bataDetails.driverAllowance,
      calculationDetails: `${d} km × ₹${dropRatePerKm}/km`,
      billedKm: d,
      isOneWay: true,
      lines,
    });
  } else {
    // Hourly packages
    const evaluateHourly = (pkgHr, pkgKm, baseRate) => {
      if (!baseRate || baseRate <= 0) return;
      const extraHrs = Math.ceil(Math.max(0, h - pkgHr));
      const extraKms = Math.ceil(Math.max(0, totalDriveKm - pkgKm));
      const maxExt = hourly?.maxExtraHrs !== undefined && hourly?.maxExtraHrs !== "" ? Number(hourly.maxExtraHrs) : 3;
      if (maxExt > 0 && extraHrs > maxExt) return;
      const extraHrCharge = extraHrs * (Number(hourly.extraHour) || 0);
      const extraKmCharge = extraKms * (Number(hourly.extraKm) || 0);
      if (extraHrCharge + extraKmCharge > baseRate) return;
      const hourlyBilledKm = pkgKm + extraKms;
      const lines = [{ label: `${pkgHr} Hr Package`, value: baseRate }];
      if (extraHrs > 0) lines.push({ label: `Extra ${extraHrs} hr`, value: extraHrCharge });
      if (extraKms > 0) lines.push({ label: `Extra ${extraKms} km`, value: extraKmCharge });
      candidates.push({
        mode: `Hourly Package (${pkgHr} Hrs / ${pkgKm} KMs)`,
        baseFare: baseRate + extraHrCharge + extraKmCharge,
        driverBata: 0, nightAllowance: 0, driverAllowance: 0,
        calculationDetails: `${pkgHr}Hr pkg + extras`,
        billedKm: hourlyBilledKm, isOneWay: false, lines,
      });
    };
    evaluateHourly(4, 40, Number(hourly.pkg4hr));
    evaluateHourly(8, 80, Number(hourly.pkg8hr));
    /* No 12-hour package (owner's decision, 18 Oct 2026 - the local market has none):
       past 8 hours a trip is priced as 8 hours plus extras, day rent or per km,
       whichever totals least. A card that still holds pkg12hr is ignored. */

    // Day rent
    if (Number(dayRent.dayRate) > 0 || Number(dayRent.dayRate24) > 0) {
      const evaluateDayRent = (blocks24, blocks12) => {
        const rate24 = Number(dayRent.dayRate24) || Number(dayRent.dayRate) * 2;
        const rate12 = Number(dayRent.dayRate) || 0;
        if (blocks24 === 0 && blocks12 === 0) return;
        const allowedHrs = blocks24 * 24 + blocks12 * 12;
        const extraHrs = Math.ceil(Math.max(0, h - allowedHrs));
        const maxExt = dayRent?.maxExtraHrs !== undefined && dayRent?.maxExtraHrs !== "" ? Number(dayRent.maxExtraHrs) : 3;
        if (maxExt > 0 && extraHrs > maxExt) return;
        const extraHrCharge = extraHrs * (Number(dayRent.extraHour) || 0);
        const drBase = blocks24 * rate24 + blocks12 * rate12;
        if (rate12 > 0 && extraHrCharge > rate12) return;
        const bataDetails = calculateDriverBata(h, dayRent, nights);
        // Whole rupees: a partner's own mileage (say 15 km/l) would otherwise quote paise.
        const fuelCharge = Math.round((totalDriveKm / mileage) * fuelPricePerLiter);
        const labelParts = [];
        if (blocks24 > 0) labelParts.push(`${blocks24} Day${blocks24 > 1 ? "s" : ""} (24H)`);
        if (blocks12 > 0) labelParts.push(`${blocks12} Half-Day${blocks12 > 1 ? "s" : ""} (12H)`);
        const drLines = [{ label: `Day Rent (${labelParts.join(" + ")})`, value: drBase }];
        if (extraHrs > 0) drLines.push({ label: `Extra ${extraHrs} hr`, value: extraHrCharge });
        drLines.push(...bataDetails.lines);
        if (fuelCharge > 0) drLines.push({ label: `Fuel Charge`, value: fuelCharge });
        candidates.push({
          mode: "Day Rent",
          baseFare: drBase + fuelCharge + extraHrCharge,
          driverBata: bataDetails.total,
          nightAllowance: bataDetails.nightAllowance,
          driverAllowance: bataDetails.driverAllowance,
          calculationDetails: labelParts.join(" + ") + " + Fuel",
          billedKm: totalDriveKm, isOneWay: false, lines: drLines,
        });
      };
      const full24 = Math.floor(h / 24);
      const rem = h % 24;
      if (rem <= 12) { evaluateDayRent(full24, 0); evaluateDayRent(full24, 1); }
      else { evaluateDayRent(full24, 1); evaluateDayRent(full24 + 1, 0); }
      evaluateDayRent(0, Math.floor(h / 12));
      evaluateDayRent(0, Math.ceil(h / 12));
    }

    // Outstation
    if (Number(outstation.ratePerKm) > 0) {
      const ratePerKm = Number(outstation.ratePerKm);
      const minKms = Number(outstation.minKms) || 300;
      const evaluateOutstation = (baseDays) => {
        const extraHrs = Math.ceil(Math.max(0, h - baseDays * 24));
        const maxExt = outstation?.maxExtraHrs !== undefined && outstation?.maxExtraHrs !== "" ? Number(outstation.maxExtraHrs) : 3;
        if (maxExt > 0 && extraHrs > maxExt) return;
        const minBilledKm = minKms * baseDays;
        const billedKm = Math.max(totalDriveKm, minBilledKm);
        const osBase = billedKm * ratePerKm;
        const extraHrCharge = extraHrs * (Number(outstation.extraHour) || 0);
        const bataDetails = calculateDriverBata(h, outstation, nights);
        const osLines = [{ label: `Base Fare`, value: billedKm * ratePerKm }];
        if (extraHrs > 0) osLines.push({ label: `Extra ${extraHrs} hr`, value: extraHrCharge });
        osLines.push(...bataDetails.lines);
        candidates.push({
          mode: "Outstation (Per KM)",
          baseFare: osBase + extraHrCharge,
          driverBata: bataDetails.total,
          nightAllowance: bataDetails.nightAllowance,
          driverAllowance: bataDetails.driverAllowance,
          calculationDetails: `${billedKm} km × ₹${ratePerKm}/km`,
          billedKm, isOneWay: false, lines: osLines,
        });
      };
      const fullDays = Math.floor(h / 24) || 1;
      evaluateOutstation(fullDays);
      if (Math.ceil(h / 24) !== fullDays) evaluateOutstation(Math.ceil(h / 24));
    }
  }

  if (candidates.length === 0) return null;

  let best = candidates[0];
  let minTotal = best.baseFare + best.driverBata;
  for (let i = 1; i < candidates.length; i++) {
    const total = candidates[i].baseFare + candidates[i].driverBata;
    if (total < minTotal) { minTotal = total; best = candidates[i]; }
  }

  return { best, rates };
}

// ── PUBLIC API ────────────────────────────────────────────────────────────────

/**
 * Compute driver-facing fare.
 * Contains ONLY the driver's own rates. No Sramika margin. No Convenience Surcharge.
 *
 * @param {object} car
 * @param {number} hours
 * @param {number} distanceKm
 * @param {string} tripType
 * @param {object|null} cityPricing - City rates from admin pricing API
 * @param {object|null} platformCeilings - Same city pricing data used for TYPE B detection
 * @returns {object|null}
 */
/* What each of a partner's price boxes is charged at while it is empty.
 *
 * The suggested figure computeDriverFare falls back to for a box left blank, returned
 * in the partner's own field names (pkg0, not pkg4hr). computeDriverFare reads its
 * fallbacks from here, and the onboarding form and the dashboard price editor show
 * these as the grey number in each box - so the number a partner sees in an empty box
 * is the number a fare uses.
 *
 * Driver bata is the card's "Bata 1-12 Hrs" (bata1to12), the figure the admin edits.
 * Night halt is the card's nightHalt - its own charge, on top of the day's bata, and
 * nothing when it is 0 (the association's day-wise hire has none). An older card that
 * names no night halt keeps the older reading: the night box is the whole overnight
 * bata, its bata12to24.
 *
 * Returns null when the city card does not price this car at all.
 */
export function partnerSuggestedPrices(car, cityPricing) {
  if (!car || !cityPricing) return null;
  const catName = resolveRates(car, cityPricing).categoryName;
  if (!catName || !cityPricing[catName]) return null;
  const s = (section, field) => suggestedValue(cityPricing, catName, section, field);
  /* A card naming its own night halt has it charged on top of the bata, and a card
     naming 0 has none - for the suggestion too, whatever an older suggested card still
     holds. An older card's night box is the whole overnight figure, its bata12to24. */
  const night = (section) => {
    const named = namedNightHalt(cityPricing[catName]?.[section]);
    if (named === 0) return null;
    return named !== null ? s(section, "nightHalt") : s(section, "bata12to24") ?? s(section, "nightHalt");
  };
  return {
    outstation: {
      ratePerKm: s("outstation", "ratePerKm"),
      minKms: s("outstation", "minKms"),
      extraHour: s("outstation", "extraHour"),
      driverBata: s("outstation", "driverBata") ?? s("outstation", "bata1to12") ?? s("outstation", "bata8to12"),
      driverBataNightHalt: night("outstation"),
    },
    hourly: {
      pkg0: s("hourly", "pkg4hr"),
      pkg1: s("hourly", "pkg8hr"),
      extraHour: s("hourly", "extraHour"),
      extraKm: s("hourly", "extraKm"),
    },
    dayRent: {
      dayRate: s("dayRent", "dayRate"),
      dayRate24: s("dayRent", "dayRate24"),
      extraHour: s("dayRent", "extraHour"),
      driverBeta: s("dayRent", "driverBeta") ?? s("dayRent", "bata1to12") ?? s("dayRent", "bata8to12"),
      driverBetaNightHalt: night("dayRent"),
      fuelKmPerLtr: s("dayRent", "fuelKmPerLtr"),
    },
    dropping: {
      slab0: s("dropping", "slab50") ?? s("dropping", "slab0"),
      slab1: s("dropping", "slab75") ?? s("dropping", "slab1"),
      slab2: s("dropping", "slab100") ?? s("dropping", "slab2"),
      slab3: s("dropping", "slab125") ?? s("dropping", "slab3"),
      beyondRate: s("dropping", "beyondRate"),
    },
  };
}

/* The headline rates a car would actually be quoted at, with no trip.
 *
 * A card with no pickup set has no fare to show, and it was showing nothing at all —
 * not even the per-km rate, which it had. Reading car.pricingData directly is not the
 * answer either: two thirds of partners leave boxes empty, so a third of cards would
 * still say nothing while the engine would happily quote them at the suggested share
 * of the market rate.
 *
 * So this resolves the same way a fare does — the partner's figure when they set one,
 * the suggested share of the market rate when they did not — and the number on the
 * card is therefore the number the trip will be priced from.
 *
 * Returns nulls rather than zeros for anything unpriced: zero is a price, and "₹0/km"
 * is a worse thing to show than nothing.
 */
export function effectiveHeadlineRates(car, cityPricing) {
  const ratesInfo = resolveRates(car, cityPricing);
  const catName = ratesInfo.categoryName;
  const market = (catName && cityPricing && cityPricing[catName]) || {};
  const partner = car?.pricingData || {};

  /* What a box the partner left empty is quoted at. Set by the admin per category and
     per field on the Suggested table; until a city's is seeded, it resolves to the old
     share of the market rate, so nothing changes underfoot. */
  const sugg = (section, field) => suggestedValue(cityPricing, catName, section, field);

  const val = (partnerVal, section, field) => {
    const n = pick(partnerVal, sugg(section, field));
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  };

  void market;
  return {
    ratePerKm: val(partner.outstation?.ratePerKm, "outstation", "ratePerKm"),
    dayRate: val(partner.dayRent?.dayRate, "dayRent", "dayRate"),
    // Partner hourly slabs are stored positionally as pkg0/pkg1; the market card names
    // them by duration. Same slab, two spellings — see computeDriverFare.
    pkg8hr: val(partner.hourly?.pkg1, "hourly", "pkg8hr"),
  };
}

/* `trip.startAt` is the pickup date and time, when the caller knows it: the night halt
   then follows the admin's night hours rather than the trip's length (nightsInTrip). */
export function computeDriverFare(car, hours, distanceKm, tripType, cityPricing, trip = {}) {
  const ratesInfo = resolveRates(car, cityPricing);
  const catName = ratesInfo.categoryName;
  let partnerCityPricing = null;

  if (catName && cityPricing && cityPricing[catName]) {
    const market = cityPricing[catName];
    const partner = car.pricingData || {};
    /* EVERY box the partner filled is used, and only an empty box falls back - to the
       admin's suggested value for this exact category and field. The fallbacks come
       from partnerSuggestedPrices, which is also what the onboarding form and the
       dashboard show as the grey number in each empty box: one source, so the number a
       partner sees is the number they are charged.

       This used to take five of the partner's boxes and quietly drop the rest: their
       driver bata was ignored whenever the card had bata slabs (every card does), so
       every car was charged the MARKET bata whatever it had set; night halt, mileage and
       the one-way drop prices were saved and never read. */
    const sp = partnerSuggestedPrices(car, cityPricing);
    const sugg = (section, field) => suggestedValue(cityPricing, catName, section, field);
    const pOut = partner.outstation || {};
    const pHourly = partner.hourly || {};
    const pDay = partner.dayRent || {};
    const pDrop = partner.dropping || {};
    const mOut = market.outstation || {};
    const mHourly = market.hourly || {};
    const mDay = market.dayRent || {};
    const mDrop = market.dropping || {};

    partnerCityPricing = {
      ...cityPricing,
      [catName]: {
        ...market,
        outstation: {
          ...mOut,
          ratePerKm: pick(pOut.ratePerKm, sp.outstation.ratePerKm),
          // A distance, not a price, so it is never discounted — but a zero is still
          // read as blank, like every other field.
          minKms: pick(pOut.minKms, sp.outstation.minKms),
          extraHour: pick(pOut.extraHour, sp.outstation.extraHour),
          ...bataSlabs(pOut.driverBata, pOut.driverBataNightHalt, sp.outstation.driverBata, sp.outstation.driverBataNightHalt,
            sugg("outstation", "bata1to8"), sugg("outstation", "bata8to12"), "driverBata", mOut),
        },
        hourly: {
          ...mHourly,
          pkg4hr: pick(pHourly.pkg0, sp.hourly.pkg0),
          pkg8hr: pick(pHourly.pkg1, sp.hourly.pkg1),
          extraHour: pick(pHourly.extraHour, sp.hourly.extraHour),
          extraKm: pick(pHourly.extraKm, sp.hourly.extraKm)
        },
        dayRent: {
          ...mDay,
          dayRate: pick(pDay.dayRate, sp.dayRent.dayRate),
          dayRate24: pick(pDay.dayRate24, sp.dayRent.dayRate24),
          extraHour: pick(pDay.extraHour, sp.dayRent.extraHour),
          // An efficiency figure, not a price: the fuel charge divides by it.
          fuelKmPerLtr: pick(pDay.fuelKmPerLtr, sp.dayRent.fuelKmPerLtr),
          ...bataSlabs(pDay.driverBeta, pDay.driverBetaNightHalt, sp.dayRent.driverBeta, sp.dayRent.driverBetaNightHalt,
            sugg("dayRent", "bata1to8"), sugg("dayRent", "bata8to12"), "driverBeta", mDay),
        },
        // The partner's drop boxes are stored positionally (slab0 = up to 50 km); the
        // card names them by distance. Same bands, two spellings - as with pkg0/pkg4hr.
        dropping: {
          ...mDrop,
          slab25: partnerSlab25(pick(pDrop.slab0, sp.dropping.slab0), sugg("dropping", "slab25"), sp.dropping.slab0),
          slab50: pick(pDrop.slab0, sp.dropping.slab0),
          slab75: pick(pDrop.slab1, sp.dropping.slab1),
          slab100: pick(pDrop.slab2, sp.dropping.slab2),
          slab125: pick(pDrop.slab3, sp.dropping.slab3),
          beyondRate: pick(pDrop.beyondRate, sp.dropping.beyondRate),
        },
      }
    };
  } else {
    partnerCityPricing = cityPricing;
  }

  const passA = _computeTrip(car, hours, distanceKm, tripType, partnerCityPricing || cityPricing, trip);
  const passB = _computeTrip(car, hours, distanceKm, tripType, cityPricing, trip);

  if (!passA || !passB) return null;

  const partnerBest = passA.best;
  const marketBest = passB.best;
  const rates = passA.rates;

  /* The partner's price is the price.
     This clamped it to the city's market figure, so a partner who set a rate above
     the admin's card was quoted the admin's number instead of their own — silently,
     with only a console warning nobody reads. The admin card is a REFERENCE now, not
     a cap: it supplies the typical fare a customer is shown alongside the price, and
     it fills in for a partner who left a box blank. It no longer overrules someone
     who priced their own car deliberately.
     Nothing downstream needs the clamp: the discount below already floors at zero, so
     a price above the market simply shows no saving rather than a negative one, and
     the card only draws the struck-through figure when there is a saving to show. */
  const partnerFare = partnerBest.baseFare + partnerBest.driverBata;
  const marketFare = marketBest.baseFare + marketBest.driverBata;

  const discount = Math.max(0, marketFare - partnerFare);
  const discountPercent = marketFare > 0 ? Math.round((discount / marketFare) * 100) : 0;

  /* Whose saving is it?
   *
   * A partner who leaves their rate card blank is quoted at the suggested share of
   * the market rate — 80% today — so a gap appears against the market whether or not
   * they ever chose to undercut it. Eleven of thirty-one cars therefore showed
   * exactly "Save 19%", which is not eleven partners competing, it is one admin
   * setting displayed thirty-one times. Presenting that as the partner's price
   * advantage is the same trick as the Rs 1/km markup we deleted: a discount off a
   * number we picked ourselves.
   *
   * So the caller is told whether the partner typed anything at all. A saving that
   * exists only because of the prefill can then be left unsaid, and the one shown is
   * a choice somebody actually made. If they filled some boxes and not others the
   * saving is partly theirs, and that counts — they engaged with the form. */
  const partnerSetOwnRates = (() => {
    const pd = car?.pricingData;
    if (!pd || typeof pd !== "object") return false;
    for (const section of ["outstation", "hourly", "dayRent", "dropping"]) {
      const vals = pd[section];
      if (!vals || typeof vals !== "object") continue;
      for (const v of Object.values(vals)) {
        if (v !== "" && v != null && Number(v) > 0) return true;
      }
    }
    return false;
  })();

  const customerPays = partnerFare;
  const PLATFORM_FEE_PERCENT = 10;
  const platformFee = Math.round(customerPays * (PLATFORM_FEE_PERCENT / 100));
  const driverEarnings = customerPays - platformFee;

  return {
    baseFare: partnerBest.baseFare,
    driverAllowance: partnerBest.driverAllowance,
    nightAllowance: partnerBest.nightAllowance,
    subtotal: partnerFare,
    
    partnerFare,
    marketFare,
    partnerSetOwnRates,
    discount,
    discountPercent,

    cabPartnerDiscountAmount: discount,
    cabPartnerDisplayName: car.ownerDriver?.name || car.carLabel || "Partner",
    
    customerPays,
    platformFee,
    driverEarnings,

    mode: partnerBest.mode,
    calculationDetails: partnerBest.calculationDetails,
    billedKm: partnerBest.billedKm,
    isOneWay: partnerBest.isOneWay,
    tripHours: Math.round((Math.max(1, Number(hours) || 0)) * 10) / 10,
    categoryName: rates.categoryName,
    lines: partnerBest.lines.map((l) => ({ ...l, value: Number(l.value) })),
  };
}

/**
 * Compute customer-facing fare.
 *
 * Listed price is the admin's real market rate. The only saving shown is the genuine
 * one: the gap between that and what this partner actually charges.
 *
 * There is no ₹1/km markup and no "Sramika Discount" any more. That mechanism raised
 * the base fare by ₹1/km and then presented the same ₹1/km back as a discount, so the
 * customer paid exactly the listed price either way and the saving was arithmetic on
 * a number we had just inflated. The real difference against the typical fare does
 * the job honestly, which is why nothing replaced it.
 *
 * @param {object} driverFare - output of computeDriverFare()
 * @param {number} estimatedKm - unused, kept so existing callers keep working
 * @param {boolean} isDiscountRemoved - unused, kept so existing callers keep working
 * @returns {object}
 */
export function computeCustomerFare(driverFare, estimatedKm, isDiscountRemoved = false, cityPricing = null) {
  if (!driverFare) return null;

  const lines = [...(driverFare.lines || [])];
  const listedPrice = driverFare.marketFare || driverFare.subtotal;
  const cabPartnerDiscountAmount = driverFare.discount || 0;
  const finalCustomerAmount = driverFare.partnerFare || driverFare.subtotal;

  return {
    ...driverFare,
    lines,
    listedPrice,
    finalCustomerAmount,
    discountPercent: driverFare.discountPercent || 0,
  };
}

/**
 * Legacy wrapper — preserves backward compatibility with existing calculateFare() callers.
 * New code should use computeDriverFare + computeCustomerFare directly.
 */
export function calculateFare(car, hours, distanceKm, tripType, cityPricing, trip = {}) {
  const driverFare = computeDriverFare(car, hours, distanceKm, tripType, cityPricing, trip);
  if (!driverFare) return null;

  const customerFare = computeCustomerFare(driverFare, driverFare.billedKm, false, cityPricing);

  const refFare = Math.round(customerFare.listedPrice || customerFare.finalCustomerAmount);
  const drvFare = Math.round(customerFare.finalCustomerAmount);
  const drvDisc = Math.round(customerFare.cabPartnerDiscountAmount || 0);

  return {
    // 🚀🚀 Lead Marketplace Standard Fields (Phase 2.1 Prepared) 🚀🚀
    referenceFare: refFare,
    driverFare: drvFare,
    driverDiscount: drvDisc,
    finalPrice: drvFare,
    partnerFare: Math.round(driverFare.customerPays),
    // Whether the gap against the market is a partner's own choice or our prefill.
    // A card showing "Save 19%" on eleven cars was showing one admin setting, not
    // eleven partners competing.
    partnerSetOwnRates: driverFare.partnerSetOwnRates === true,

    // ── Deprecated Commission Model Fields (Retained for Backward Compatibility until Phase 2.3) ──
    original: customerFare.listedPrice,
    final: customerFare.finalCustomerAmount,
    totalDiscountPct: customerFare.discountPercent,
    breakdown: {
      mode: driverFare.mode,
      calculationDetails: driverFare.calculationDetails,
      baseFare: driverFare.baseFare,
      driverBeta: driverFare.driverAllowance,
      nightAllowance: driverFare.nightAllowance,
      discountAmount: customerFare.cabPartnerDiscountAmount,
      ownerDiscountPercent: 0,
      totalDiscount: customerFare.cabPartnerDiscountAmount,
      totalDiscountPct: customerFare.discountPercent,
      billedKm: driverFare.billedKm,
      isOneWay: driverFare.isOneWay,
      tripHours: driverFare.tripHours,
      totalKmBothWays: driverFare.isOneWay ? driverFare.billedKm : Math.round(driverFare.billedKm),
      lines: customerFare.lines,
      categoryName: driverFare.categoryName,
      cabPartnerDiscountAmount: customerFare.cabPartnerDiscountAmount,
      cabPartnerDisplayName: driverFare.cabPartnerDisplayName,
      // Lead Marketplace standard fields in breakdown
      referenceFare: refFare,
      driverFareAmount: drvFare,
      driverDiscount: drvDisc,
      finalPrice: drvFare,
      // Customer output
      customerFare,
      driverFare,
    },
  };
}
